import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useAdminCapability } from "../useAdminCapability";
import { useOwnerAudit, useOwnerCapabilities, useOwnerSupplyCheck } from "./useMarketplace";
import { SupplyIntegrityPanel } from "./SupplyIntegrityPanel";
import { OwnerActionsPanel } from "./OwnerActionsPanel";
import { OwnerAuditPanel } from "./OwnerAuditPanel";
import { OwnerActionDialog } from "./OwnerActionDialog";
import { OwnerActionParamsForm } from "./OwnerActionParamsForm";
import { PrivateSaleCancelDialog } from "./PrivateSaleCancelDialog";
import { EmptyState, ErrorState } from "./primitives";
import { actionAvailability, type OwnerAction } from "./owner-console-logic";
import { marketplaceApi, MarketplaceError, type PrivateSale } from "../../lib/marketplace-api";
import { useT } from "../../i18n";
import type { ZoneKey } from "./mkt-format";

interface PendingConfirm {
  action: OwnerAction;
  zone: ZoneKey;
  slots: number;
  body: Record<string, unknown>;
}

function apiAction(a: OwnerAction): "release" | "relock" | "private-sale" {
  return a === "private_sale" ? "private-sale" : a;
}

export function OwnerConsole() {
  const t = useT();
  const { isAdmin, loading: adminLoading } = useAdminCapability();
  const auditQuery = useOwnerAudit(isAdmin);
  const capQuery = useOwnerCapabilities(isAdmin);
  const [checkOwner, setCheckOwner] = useState<string | null>(null);
  const supplyQuery = useOwnerSupplyCheck(isAdmin ? checkOwner : null);
  const [paramsFor, setParamsFor] = useState<OwnerAction | null>(null);
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const [cancelTarget, setCancelTarget] = useState<PrivateSale | null>(null);

  const action = useMutation({
    mutationFn: (c: PendingConfirm) => marketplaceApi.ownerAction(apiAction(c.action), c.body),
    onSuccess: () => {
      setPending(null);
      auditQuery.refetch();
      capQuery.refetch();
    },
  });

  const cancelPs = useMutation({
    mutationFn: (saleId: string) => marketplaceApi.cancelPrivateSale(saleId),
    onSuccess: () => {
      // Only fires AFTER the backend confirms a mined receipt → refresh the
      // authoritative overview so the row reflects the cancelled state.
      auditQuery.refetch();
    },
  });

  if (adminLoading) {
    return (
      <div
        data-testid="owner-loading"
        className="py-24 text-center font-mono text-[11px] tracking-[0.2em] uppercase text-text-muted animate-pulse"
      >
        {t("marketplace.owner.verifyingAccess")}
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <EmptyState
        testId="owner-access-denied"
        title={t("marketplace.owner.accessDeniedTitle")}
        hint={t("marketplace.owner.accessDeniedHint")}
      />
    );
  }
  if (auditQuery.isError || capQuery.isError) {
    return (
      <ErrorState
        testId="owner-error"
        title={t("marketplace.owner.loadErrorTitle")}
        detail={(auditQuery.error as Error)?.message ?? (capQuery.error as Error)?.message}
        onRetry={() => {
          auditQuery.refetch();
          capQuery.refetch();
        }}
      />
    );
  }
  const audit = auditQuery.data;
  const caps = capQuery.data;
  if (!audit || !caps) {
    return (
      <div
        data-testid="owner-loading"
        className="py-24 text-center font-mono text-[11px] tracking-[0.2em] uppercase text-text-muted animate-pulse"
      >
        {t("marketplace.owner.loading")}
      </div>
    );
  }

  return (
    <div data-testid="owner-console" className="space-y-12">
      <SupplyIntegrityPanel audit={audit} />
      <OwnerActionsPanel
        capabilities={caps}
        onOpen={(a) => {
          action.reset();
          setPending(null);
          setParamsFor(a);
        }}
      />
      <OwnerAuditPanel
        privateSales={audit.private_sales}
        cancelSupported={audit.private_sale_cancel_supported}
        onRequestCancel={(sale) => {
          cancelPs.reset();
          setCancelTarget(sale);
        }}
        onLookup={(w) => setCheckOwner(w.trim().toLowerCase() || null)}
        supplyResult={supplyQuery.data}
        supplyLoading={supplyQuery.isFetching}
        supplyError={supplyQuery.isError ? ((supplyQuery.error as Error)?.message ?? "lookup failed") : null}
      />
      {cancelTarget && (
        <PrivateSaleCancelDialog
          sale={cancelTarget}
          submitting={cancelPs.isPending}
          succeeded={cancelPs.isSuccess}
          resultError={
            cancelPs.isError
              ? ((cancelPs.error as MarketplaceError)?.message ?? t("marketplace.owner.cancelPsUnsupported"))
              : null
          }
          onClose={() => {
            setCancelTarget(null);
            cancelPs.reset();
          }}
          onConfirm={() => cancelPs.mutate(cancelTarget.sale_id)}
        />
      )}
      {paramsFor && (
        <OwnerActionParamsForm
          action={paramsFor}
          onCancel={() => setParamsFor(null)}
          onReady={({ body, slots, zone }) => {
            setPending({ action: paramsFor, body, slots, zone });
            setParamsFor(null);
          }}
        />
      )}
      {pending && (
        <OwnerActionDialog
          action={pending.action}
          zone={pending.zone}
          slots={pending.slots}
          availability={actionAvailability(caps, pending.action)}
          submitting={action.isPending}
          error={action.isError ? ((action.error as Error)?.message ?? "action failed") : null}
          onClose={() => setPending(null)}
          onConfirm={() => action.mutate(pending)}
        />
      )}
    </div>
  );
}

export default OwnerConsole;
